import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  useMediaQuery,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import { api } from '../../api/api';
import { addChangeRequest, getChangeRequests } from '../../Redux/Actions/changeRequestAction';

const SIZE_KEYS = ['hot_s', 'hot_m', 'ice_s', 'ice_m'];
const SIZE_LABELS = { hot_s: 'HOT 12oz', hot_m: 'HOT 16oz', ice_s: 'ICED 16oz', ice_m: 'ICED 20oz' };
const SIZE_SHORT = { hot_s: '12oz', hot_m: '16oz', ice_s: '16oz', ice_m: '20oz' };
const CATS = [
  { key: 'espresso', label: 'ESPRESSO' },
  { key: 'brewed_coffee', label: 'BREWED' },
  { key: 'tea', label: 'TEA' },
  { key: 'cold_drink', label: 'COLD DRINKS' },
  { key: 'chocolate', label: 'CHOCOLATE' },
];

function MenuPriceEditor({ store, userId, user, subuserId }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [menuPrices, setMenuPrices] = useState([]);
  const [menuMasters, setMenuMasters] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});
  const [activeCat, setActiveCat] = useState('espresso');
  const [showSummary, setShowSummary] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [newMenuForm, setNewMenuForm] = useState({ name: '', category: 'espresso', prices: {} });
  const [orderChanged, setOrderChanged] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  // 14번: 모바일 가격 편집 팝업
  const [editPopup, setEditPopup] = useState(null); // { menuCode, menuName, sizeKey, currentValue }

  const loadData = useCallback(async () => {
    if (!store?._id) return;
    try {
      const [pr, mm] = await Promise.all([
        api.get(`/menu-prices/${store._id}`),
        api.get('/menu-master/items'),
      ]);
      setMenuPrices(pr.data || []);
      setMenuMasters(mm.data?.items || mm.data || []);
    } catch (e) { console.error(e); }
  }, [store?._id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEdit = (menuCode, sizeKey, value) => {
    const cents = Math.round(parseFloat(value || 0) * 100);
    setPriceEdits(prev => ({ ...prev, [menuCode]: { ...(prev[menuCode] || {}), [sizeKey]: cents } }));
  };

  const changeSummary = Object.entries(priceEdits).flatMap(([code, sizes]) => {
    const mp = menuPrices.find(m => m.menuCode === code);
    if (!mp) return [];
    return Object.entries(sizes).filter(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0)
      .map(([k, v]) => ({ menuCode: code, menuName: mp.menuName, sizeKey: k, oldPrice: mp.prices?.[k] || 0, newPrice: v }));
  });

  const submitChanges = async () => {
    if (changeSummary.length === 0) return;
    const details = changeSummary.map(c =>
      `${c.menuName} ${SIZE_LABELS[c.sizeKey]}: $${(c.oldPrice / 100).toFixed(2)} → $${(c.newPrice / 100).toFixed(2)}`
    ).join('\n');
    await dispatch(addChangeRequest({
      user: userId, store: store._id,
      requestedBy: subuserId || undefined,
      requestedByName: user?.username || '',
      type: 'price_change',
      changeDetails: `가격 변경 요청 (${changeSummary.length}건):\n${details}`,
      priceChanges: changeSummary,
    }));
    setPriceEdits({});
    setShowSummary(false);
    dispatch(getChangeRequests(userId, { subuserId }));
  };

  const submitNewMenu = async () => {
    if (!newMenuForm.name.trim()) return;
    const priceDesc = Object.entries(newMenuForm.prices).filter(([, v]) => v > 0)
      .map(([k, v]) => `${SIZE_LABELS[k]}: $${(v / 100).toFixed(2)}`).join(', ');
    await dispatch(addChangeRequest({
      user: userId, store: store._id,
      requestedBy: subuserId || undefined,
      requestedByName: user?.username || '',
      type: 'new_menu', menuName: newMenuForm.name,
      changeDetails: `신규 메뉴 추가 요청: ${newMenuForm.name}\nCategory: ${newMenuForm.category}\n${priceDesc || 'Prices TBD'}`,
    }));
    setNewMenuForm({ name: '', category: 'espresso', prices: {} });
    setShowNewMenu(false);
    dispatch(getChangeRequests(userId, { subuserId }));
  };

  // ── 드래그앤드롭 ──
  const getCatMenus = () => {
    return menuPrices.filter(mp => {
      const m = menuMasters.find(mm => mm.code === mp.menuCode);
      return m?.category === activeCat;
    }).sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
  };

  const handleDragStart = (idx) => setDragIdx(idx);

  const handleDragOver = (e, targetIdx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) return;
    const items = getCatMenus();
    const moved = items[dragIdx];
    const updated = [...items];
    updated.splice(dragIdx, 1);
    updated.splice(targetIdx, 0, moved);
    // displayOrder 업데이트
    const newPrices = menuPrices.map(mp => {
      const idx = updated.findIndex(u => u._id === mp._id);
      if (idx >= 0) return { ...mp, displayOrder: idx };
      return mp;
    });
    setMenuPrices(newPrices);
    setDragIdx(targetIdx);
    setOrderChanged(true);
  };

  const handleDragEnd = () => setDragIdx(null);

  // 모바일 터치 드래그
  const touchStart = useRef(null);
  const handleTouchStart = (idx, e) => {
    touchStart.current = { idx, y: e.touches[0].clientY };
  };
  const handleTouchMove = (e) => {
    if (!touchStart.current) return;
    const el = document.elementFromPoint(e.touches[0].clientX, e.touches[0].clientY);
    const card = el?.closest?.('[data-drag-idx]');
    if (card) {
      const targetIdx = parseInt(card.dataset.dragIdx);
      if (targetIdx !== touchStart.current.idx) {
        handleDragOver(e, targetIdx);
        touchStart.current.idx = targetIdx;
      }
    }
  };
  const handleTouchEnd = () => { touchStart.current = null; handleDragEnd(); };

  const saveOrder = async () => {
    const items = getCatMenus();
    const order = items.map((mp, i) => ({ menuCode: mp.menuCode, displayOrder: i }));
    try {
      await api.patch(`/menu-prices/reorder/${store._id}`, { order });
      setOrderChanged(false);
    } catch (e) { console.error(e); }
  };

  const catMenus = getCatMenus();
  const fmt = (cents) => (cents / 100).toFixed(2);

  const catCounts = CATS.map(c => ({
    ...c,
    count: menuPrices.filter(mp => menuMasters.find(m => m.code === mp.menuCode)?.category === c.key).length,
  }));

  // ══════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════
  return (
    <Box>
      <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(27,94,59,0.08)', mb: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: isMobile ? 16 : 18, color: '#1B5E3B', fontFamily: "'Playfair Display',serif" }}>
            {store?.name || store?.tljNo} — Menu
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {orderChanged && (
              <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={saveOrder}
                sx={{ background: '#C5A55A', fontSize: 11, borderRadius: 2, '&:hover': { background: '#B8963D' } }}>
                순서 저장
              </Button>
            )}
            <Button size="small" variant="outlined" startIcon={<AddCircleOutlineIcon />}
              onClick={() => setShowNewMenu(true)}
              sx={{ borderColor: '#1B5E3B', color: '#1B5E3B', fontSize: 11, borderRadius: 2 }}>New</Button>
          </Box>
        </Box>

        {/* Category pills */}
        <Box sx={{ display: 'flex', gap: 0.8, mb: 2, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' } }}>
          {catCounts.map(c => (
            <Box key={c.key} onClick={() => setActiveCat(c.key)} sx={{
              flexShrink: 0, px: 1.5, py: 0.6, borderRadius: '16px', fontSize: 11, fontWeight: 600,
              cursor: 'pointer', background: activeCat === c.key ? '#1B5E3B' : '#f5f5f5',
              color: activeCat === c.key ? '#fff' : '#757575', transition: '0.15s',
            }}>{c.label} ({c.count})</Box>
          ))}
        </Box>

        <Typography sx={{ fontSize: 11, color: '#999', mb: 1 }}>
          ≡ 드래그하여 순서 변경 · 가격을 수정하면 하단 요약에 표시됩니다
        </Typography>

        {/* Table / Cards */}
        {isMobile ? (
          /* ── MOBILE: Card Layout (스크롤 가능) ── */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '60vh', overflowY: 'auto', pb: changeSummary.length > 0 ? '80px' : 0 }}
            onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
            {catMenus.map((mp, idx) => {
              const edits = priceEdits[mp.menuCode] || {};
              const hasChange = Object.entries(edits).some(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0);
              return (
                <Box key={mp._id} data-drag-idx={idx}
                  onTouchStart={e => handleTouchStart(idx, e)}
                  sx={{
                    background: hasChange ? '#FFFDE7' : '#fff',
                    border: `1.5px solid ${hasChange ? '#C5A55A' : dragIdx === idx ? '#1B5E3B' : '#eee'}`,
                    borderRadius: '12px', overflow: 'hidden',
                    opacity: dragIdx === idx ? 0.6 : 1, transition: 'opacity 0.15s',
                  }}>
                  {/* Card header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', p: '10px 12px 6px', gap: 1 }}>
                    <DragIndicatorIcon sx={{ fontSize: 18, color: '#ccc', cursor: 'grab', touchAction: 'none' }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{mp.menuName}</Typography>
                    {hasChange && <Box sx={{ fontSize: 9, fontWeight: 700, background: '#F5EDD6', color: '#C5A55A', px: 1, py: 0.2, borderRadius: 1 }}>CHANGED</Box>}
                  </Box>
                  {/* Price grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${SIZE_KEYS.filter(k => mp.prices?.[k] !== undefined).length}, 1fr)`, gap: 0, p: '0 12px 10px' }}>
                    {SIZE_KEYS.filter(k => mp.prices?.[k] !== undefined).map(k => {
                      const isHot = k.startsWith('hot');
                      const current = mp.prices[k];
                      const edited = edits[k];
                      const changed = edited !== undefined && edited !== current && edited > 0;
                      return (
                        <Box key={k} sx={{ textAlign: 'center', p: '4px 3px', borderRight: '1px solid #f5f5f5', '&:last-child': { borderRight: 'none' } }}>
                          <Typography sx={{ fontSize: 9, fontWeight: 700, color: isHot ? '#C62828' : '#1565C0', mb: 0.3 }}>
                            {isHot ? '☕' : '🧊'} {SIZE_SHORT[k]}
                          </Typography>
                          <Box
                            onClick={() => setEditPopup({ menuCode: mp.menuCode, menuName: mp.menuName, sizeKey: k, currentValue: fmt(edited !== undefined ? edited : current) })}
                            sx={{
                              width: '100%', padding: '10px 4px',
                              border: `1.5px solid ${changed ? '#C5A55A' : '#e8e8e8'}`,
                              borderRadius: '8px', textAlign: 'center', fontSize: 17, fontWeight: 700,
                              fontFamily: 'inherit', cursor: 'pointer',
                              background: changed ? '#F5EDD6' : '#fafafa',
                              '&:active': { background: '#E8F5E9' },
                            }}>
                            {fmt(edited !== undefined ? edited : current)}
                          </Box>
                          {changed && <Typography sx={{ fontSize: 9, color: '#C62828', textDecoration: 'line-through' }}>${fmt(current)}</Typography>}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          /* ── DESKTOP: Table Layout ── */
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#1B5E3B', color: '#fff' }}>
                  <th style={{ ...thS, width: 30 }}></th>
                  <th style={{ ...thS, textAlign: 'left', minWidth: 160 }}>Product</th>
                  <th colSpan={2} style={{ ...thS, background: '#C62828' }}>HOT</th>
                  <th colSpan={2} style={{ ...thS, background: '#1565C0' }}>ICED</th>
                </tr>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={thS2}></th>
                  <th style={thS2}></th>
                  <th style={thS2}>12oz</th><th style={thS2}>16oz</th>
                  <th style={thS2}>16oz</th><th style={thS2}>20oz</th>
                </tr>
              </thead>
              <tbody>
                {catMenus.map((mp, idx) => {
                  const edits = priceEdits[mp.menuCode] || {};
                  const hasChange = Object.entries(edits).some(([k, v]) => v !== (mp.prices?.[k] || 0) && v > 0);
                  return (
                    <tr key={mp._id} draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={e => handleDragOver(e, idx)}
                      onDragEnd={handleDragEnd}
                      style={{ borderBottom: '1px solid #f0f0f0', background: hasChange ? '#FFFDE7' : dragIdx === idx ? '#E8F5E9' : '#fff', cursor: 'grab' }}>
                      <td style={{ textAlign: 'center', padding: '6px', color: '#ccc' }}>
                        <DragIndicatorIcon sx={{ fontSize: 16 }} />
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{mp.menuName}</td>
                      {SIZE_KEYS.map(k => {
                        const price = mp.prices?.[k];
                        const isHot = k.startsWith('hot');
                        if (price === undefined) return <td key={k} style={{ textAlign: 'center', color: '#ddd' }}>—</td>;
                        const edited = edits[k];
                        const changed = edited !== undefined && edited !== price && edited > 0;
                        return (
                          <td key={k} style={{ textAlign: 'center', padding: '6px' }}>
                            <input type="number" step="0.05" defaultValue={fmt(price)}
                              onBlur={e => handleEdit(mp.menuCode, k, e.target.value)}
                              style={{
                                width: 68, padding: '5px 6px', border: `1px solid ${changed ? '#C5A55A' : '#ddd'}`,
                                borderRadius: 5, textAlign: 'center', fontSize: 13, fontFamily: 'inherit',
                                background: changed ? '#F5EDD6' : '#fafafa',
                              }} />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>

      {/* Change Summary */}
      {changeSummary.length > 0 && (
        <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 3, border: '2px solid #C5A55A', background: '#FDF8F0', mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#5D4037', mb: 1 }}>
            변경 요약 ({changeSummary.length}건)
          </Typography>
          {changeSummary.map((c, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: 12 }}>
              <span style={{ fontWeight: 500 }}>{c.menuName} <span style={{ color: '#888' }}>{SIZE_SHORT[c.sizeKey]}</span></span>
              <span>
                <span style={{ color: '#C62828', textDecoration: 'line-through' }}>${fmt(c.oldPrice)}</span>
                <span style={{ color: '#C5A55A', margin: '0 4px' }}>→</span>
                <span style={{ color: '#1B5E3B', fontWeight: 700 }}>${fmt(c.newPrice)}</span>
              </span>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => setPriceEdits({})} sx={{ color: '#888', fontSize: 12 }}>초기화</Button>
            <Button size="small" variant="contained" onClick={submitChanges}
              sx={{ background: '#1B5E3B', fontSize: 12, borderRadius: 2, '&:hover': { background: '#2D8B5E' } }}>
              변경 요청 제출
            </Button>
          </Box>
        </Paper>
      )}

      {/* Mobile bottom bar */}
      {isMobile && changeSummary.length > 0 && (
        <Box sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff',
          borderTop: '1px solid #e8e8e8', p: '10px 16px', pb: 'max(10px, env(safe-area-inset-bottom))',
          display: 'flex', gap: 1, zIndex: 90,
        }}>
          <Button onClick={() => setPriceEdits({})} sx={{ flex: 1, py: 1.2, borderRadius: 3, background: '#f5f5f5', color: '#757575', fontWeight: 600, fontSize: 13 }}>Reset</Button>
          <Button onClick={submitChanges}
            sx={{ flex: 2, py: 1.2, borderRadius: 3, background: '#1B5E3B', color: '#fff', fontWeight: 600, fontSize: 13, '&:hover': { background: '#2D8B5E' } }}>
            Submit ({changeSummary.length})
          </Button>
        </Box>
      )}

      {/* FAB */}
      {isMobile && changeSummary.length > 0 && (
        <Box sx={{
          position: 'fixed', bottom: 70, right: 16, width: 48, height: 48, borderRadius: '50%',
          background: '#C5A55A', color: '#fff', fontWeight: 700, fontSize: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(197,165,90,0.4)', zIndex: 80,
        }}>{changeSummary.length}</Box>
      )}

      {/* 14번: 모바일 가격 편집 팝업 */}
      {editPopup && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1300,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: 2,
        }} onClick={() => setEditPopup(null)}>
          <Paper sx={{ p: 3, borderRadius: 3, width: '100%', maxWidth: 320, textAlign: 'center' }}
            onClick={e => e.stopPropagation()}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>{editPopup.menuName}</Typography>
            <Typography sx={{ fontSize: 13, color: editPopup.sizeKey.startsWith('hot') ? '#C62828' : '#1565C0', fontWeight: 600, mb: 2 }}>
              {editPopup.sizeKey.startsWith('hot') ? '☕ HOT' : '🧊 ICED'} {SIZE_SHORT[editPopup.sizeKey]}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#888', mb: 1 }}>현재 가격</Typography>
            <input
              id="price-edit-input"
              type="number"
              step="0.05"
              autoFocus
              defaultValue={editPopup.currentValue}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleEdit(editPopup.menuCode, editPopup.sizeKey, e.target.value);
                  setEditPopup(null);
                }
              }}
              style={{
                width: '100%', padding: '16px', border: '2px solid #1B5E3B',
                borderRadius: 12, textAlign: 'center', fontSize: 36, fontWeight: 800,
                fontFamily: 'inherit', background: '#f8faf9', outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <Typography sx={{ fontSize: 11, color: '#999', mt: 1, mb: 2 }}>
              $ 단위로 입력 (예: 4.75)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button fullWidth onClick={() => setEditPopup(null)}
                sx={{ py: 1.2, borderRadius: 3, background: '#f5f5f5', color: '#757575', fontWeight: 600 }}>
                취소
              </Button>
              <Button fullWidth id="price-edit-confirm" onClick={() => {
                const input = document.getElementById('price-edit-input');
                const val = input?.value || editPopup.currentValue;
                handleEdit(editPopup.menuCode, editPopup.sizeKey, val);
                setEditPopup(null);
              }}
                sx={{ py: 1.2, borderRadius: 3, background: '#1B5E3B', color: '#fff', fontWeight: 600, '&:hover': { background: '#2D8B5E' } }}>
                확인
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {/* New Menu Dialog */}
      <Dialog open={showNewMenu} onClose={() => setShowNewMenu(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#1B5E3B', borderBottom: '3px solid #1B5E3B' }}>
          신규 메뉴 추가 요청
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField size="small" label="메뉴 이름 *" value={newMenuForm.name}
              onChange={e => setNewMenuForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <FormControl fullWidth size="small">
              <InputLabel>카테고리</InputLabel>
              <Select value={newMenuForm.category} label="카테고리"
                onChange={e => setNewMenuForm(f => ({ ...f, category: e.target.value }))}>
                {CATS.map(c => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
              </Select>
            </FormControl>
            {SIZE_KEYS.map(k => (
              <TextField key={k} size="small" type="number" label={`${SIZE_LABELS[k]} ($)`}
                inputProps={{ step: '0.05' }}
                onChange={e => setNewMenuForm(f => ({
                  ...f, prices: { ...f.prices, [k]: Math.round(parseFloat(e.target.value || 0) * 100) }
                }))} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowNewMenu(false)} sx={{ color: '#888' }}>취소</Button>
          <Button variant="contained" onClick={submitNewMenu} disabled={!newMenuForm.name.trim()}
            sx={{ background: '#1B5E3B', borderRadius: 2 }}>요청 제출</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const thS = { padding: '10px 12px', textAlign: 'center', fontWeight: 600, fontSize: 11, letterSpacing: 0.5 };
const thS2 = { padding: '6px 8px', textAlign: 'center', fontSize: 10, color: '#888', fontWeight: 600, background: '#f9f9f9' };

export default MenuPriceEditor;
